import clsx from "clsx";
import TwoSidebars from "../../layouts/TwoSidebars/TwoSidebars";
import { useNavigate } from "react-router-dom";

import PageTitle from "../../components/PageTitle/PageTitle";
import ButtonGroup from "../../components/ButtonGroup/ButtonGroup";
import {
  MdInfoOutline,
  MdOutlineNavigateBefore,
  MdSettings,
} from "react-icons/md";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import SettingsBlocks from "../../components/SettingsBlocks/SettingsBlocks";
import { BUTTON_STYLES, ICON_SIZES } from "../../helpers/constants";
import useDarkMode from "../../hooks/useDarkMode";
import useMobile from "../../hooks/useMobile";
import useGeneralOptionsDropdownMenu from "../../hooks/useGeneralOptionsDropdownMenu";
import CommonTable from "src/components/Tables/CommonTable";
import { ICellRendererParams } from "ag-grid-community";
import useCurrentUser from "src/hooks/useCurrentUser";
import { AnimatePresence } from "framer-motion";
import useAllTransactions from "src/hooks/useAllTransactions";
import { rgbaObjectToString } from "src/helpers/utils";
import Icon from "src/components/Icon/Icon";
import ClientProviderInfoModal from "src/components/ClientProviderInfoModal/ClientProviderInfoModal";
import LoadingWithTextPage from "src/components/LoadingWithText/LoadingWithTextPage";
import { ESourcesTableKeys } from "src/components/Tables/tableIds";

const SourcesPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const { isLoadingAccounts } = useCurrentUser();
  const [showSourceInfo, setShowSourceInfo] = useState<boolean>(false);
  const [sourceToShowDetailsOf, setSourceToShowDetailsOf] =
    useState<string>("");

  const generalOptionsDropdown = useGeneralOptionsDropdownMenu();
  const {
    getSourceCustomSettings,
    allSourcesObject,
    isLoadingAllTransactions,
    isLoadingCustomizedSources,
  } = useAllTransactions();

  const { isMobile } = useMobile();
  const { t } = useTranslation();

  const navigate = useNavigate();
  const columns = useMemo(
    () => [
      {
        field: "label",
        flex: 1,
        minWidth: 180,
        headerName: "ORIGEN / DESTINO",
        cellRenderer: "CellWithBackgroundRenderer",
        cellRendererParams: (params: ICellRendererParams) => {
          const colors = {
            background: "#555",
            color: "#fff",
          };

          if (params?.data) {
            colors.background = rgbaObjectToString(
              getSourceCustomSettings(params?.data?.label).bg
            );
            colors.color = rgbaObjectToString(
              getSourceCustomSettings(params?.data?.label).color
            );
          }

          return {
            params: {
              ...params,
              value: params?.data?.label || "Sin categoría",
            },
            custom: {
              ...colors,
              onClick: () => {
                navigate(`/settings/sources/${params?.data?.value}`);
              },
            },
          };
        },
      },
      {
        headerName: "ICONO",
        cellRenderer: (params: ICellRendererParams) => {
          return (
            <div className={clsx(["flex", "items-center", "h-full"])}>
              <Icon
                size={ICON_SIZES.MD}
                name={getSourceCustomSettings(params?.data?.label).icon}
                className="cursor-pointer"
                onClick={() => {
                  navigate(`/settings/sources/${params?.data?.value}`);
                }}
              />
            </div>
          );
        },
      },
      {
        headerName: "FONDO",
        cellRenderer: "CellColorRenderer",
        cellRendererParams: (params: ICellRendererParams) => {
          return {
            params,
            custom: {
              background: rgbaObjectToString(
                getSourceCustomSettings(params?.data?.label).bg
              ),
              onClick: () => {
                navigate(`/settings/sources/${params?.data?.value}`);
              },
            },
          };
        },
      },
      {
        headerName: "TEXTO",
        cellRenderer: "CellColorRenderer",
        cellRendererParams: (params: ICellRendererParams) => {
          return {
            params,
            custom: {
              background: rgbaObjectToString(
                getSourceCustomSettings(params?.data?.label).color
              ),
              onClick: () => {
                navigate(`/settings/sources/${params?.data?.value}`);
              },
            },
          };
        },
      },

      {
        cellRenderer: "CellWithButtonsRenderer",
        headerName: "ACCIONES",
        width: 80,
        cellRendererParams: (params: any) => {
          return {
            buttons: [
              {
                icon: <MdInfoOutline size={ICON_SIZES.SM} />,
                onClick: () => {
                  setSourceToShowDetailsOf(params?.data?.label);
                  setShowSourceInfo(true);
                },
                className: "grid-button",
              },
            ],
          };
        },
      },
    ],
    [allSourcesObject]
  );

  const isLoadingSomething = useMemo(
    () =>
      [
        !!isLoadingAllTransactions,
        !!isLoadingAccounts,
        !!isLoadingCustomizedSources,
      ]?.includes(true),
    [isLoadingAllTransactions, isLoadingAccounts, isLoadingCustomizedSources]
  );

  const { isEnabled: isDarkMode } = useDarkMode();

  return (
    <>
      <TwoSidebars
        isLoading={isLoadingSomething}
        right={
          isLoadingSomething ? null : isMobile ? null : (
            <SettingsBlocks layout="vertical" columns={isMobile ? 2 : 1} />
          )
        }
      >
        <div
          className={clsx([
            "flex",
            "flex-col",
            "w-full",
            "gap-2",
            "md:gap-2",
            "h-full",
          ])}
        >
          <AnimatePresence>
            {isLoadingSomething ? (
              <LoadingWithTextPage />
            ) : (
              <>
                <PageTitle
                  titleLink="/settings"
                  icon={<MdSettings size={ICON_SIZES.MD} />}
                  title={t("settings.title")}
                  subtitle={t("Orígenes y Destinos")}
                  rightSide={
                    <>
                      <ButtonGroup
                        align="right"
                        gap={1}
                        buttons={[
                          {
                            icon: (
                              <MdOutlineNavigateBefore size={ICON_SIZES.SM} />
                            ),
                            className: isMobile ? BUTTON_STYLES.HEADING : "",

                            tooltip: "Atras",
                            onClick: () => {
                              navigate("/settings");
                            },
                          },
                          generalOptionsDropdown,
                        ]}
                      />
                    </>
                  }
                />

                <CommonTable
                  onSelectionChange={() => {}}
                  columns={columns}
                  data={allSourcesObject}
                  singleSelection
                  settingsKey={ESourcesTableKeys.SOURCES_LIST}
                />
              </>
            )}
          </AnimatePresence>
        </div>
      </TwoSidebars>

      {!!(showSourceInfo && !!sourceToShowDetailsOf) && (
        <ClientProviderInfoModal
          onClose={() => setShowSourceInfo(false)}
          clientProvider={sourceToShowDetailsOf}
        />
      )}
    </>
  );
};

export default SourcesPage;
